import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, Fragment as _Fragment, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, renderList as _renderList } from "./../../vue/dist/vue.runtime.esm-bundler.js";
const _hoisted_1 = {
  "font-size": "11",
  fill: "currentColor",
  style: "color: var(--color-dim)"
};
const _hoisted_2 = {
  style: "color: var(--color-accent)",
  fill: "currentColor",
  stroke: "currentColor"
};
const _hoisted_3 = {
  "font-size": "10",
  style: "color: var(--color-dim)",
  fill: "currentColor"
};
import { computed, onBeforeUnmount, onMounted, ref } from "./../../vue/dist/vue.runtime.esm-bundler.js";
const margin = 8;
const flowTop = 44;
const rowHeight = 20;
export default {
  __name: "CustomerPath",
  props: {
    segments: {
      type: Array,
      required: true
    },
    steps: {
      type: Array,
      required: true
    },
    focus: {
      type: Number,
      required: true
    },
    session: {
      type: String,
      required: true
    },
    spans: {
      type: Array,
      required: true
    },
    duration: {
      type: Number,
      required: true
    }
  },
  setup(__props) {
    const props = __props;
    const root = ref(null);
    const width = ref(720);
    const active = ref(null);
    let observer;
    onMounted(() => {
      observer = new ResizeObserver(([entry]) => {
        width.value = Math.max(320, Math.floor(entry.contentRect.width));
      });
      if (root.value) observer.observe(root.value);
    });
    onBeforeUnmount(() => observer?.disconnect());
    const wide = computed(() => width.value >= 700);
    const total = (step) => Object.values(step.counts).reduce((sum, n) => sum + n, 0);
    const format = (n) => n.toLocaleString("en-US");
    // Flow geometry. Column 0 holds the sources as separate bars; the rest are steps.
    const barWidth = computed(() => wide.value ? 14 : 10);
    const unit = computed(() => (wide.value ? 120 : 84) / total(props.steps[0]));
    const columns = computed(() => props.steps.length + 1);
    const columnWidth = computed(() => (width.value - 2 * margin) / columns.value);
    const x = (column) => margin + column * columnWidth.value + columnWidth.value / 2;
    const droppedTop = computed(() => flowTop + unit.value * total(props.steps[0]) + 36);
    // Where each segment's slice sits in a step's bar.
    const slice = (step, key) => {
      let offset = 0;
      for (const segment of props.segments) {
        if (segment.key === key) break;
        offset += step.counts[segment.key] * unit.value;
      }
      return {
        top: flowTop + offset,
        height: step.counts[key] * unit.value
      };
    };
    const sourceBars = computed(() => {
      let y = flowTop;
      return props.segments.map((segment) => {
        const height = props.steps[0].counts[segment.key] * unit.value;
        const bar = {
          segment,
          top: y,
          height
        };
        y += height + 6;
        return bar;
      });
    });
    const band = (x1, top1, h1, x2, top2, h2) => {
      const mid = (x1 + x2) / 2;
      return `M${x1},${top1} C${mid},${top1} ${mid},${top2} ${x2},${top2} L${x2},${top2 + h2} C${mid},${top2 + h2} ${mid},${top1 + h1} ${x1},${top1 + h1} Z`;
    };
    const ribbons = computed(() => {
      const list = [];
      const half = barWidth.value / 2;
      for (const [index, bar] of sourceBars.value.entries()) {
        const target = slice(props.steps[0], bar.segment.key);
        list.push({
          key: bar.segment.key,
          color: bar.segment.color,
          d: band(x(0) + half, bar.top, bar.height, x(1) - half, target.top, target.height)
        });
        void index;
      }
      for (let i = 0; i < props.steps.length - 1; i++) {
        const from = props.steps[i];
        const to = props.steps[i + 1];
        let droppedOffset = droppedTop.value;
        for (const segment of props.segments) {
          const source = slice(from, segment.key);
          const target = slice(to, segment.key);
          const leftHeight = source.height - target.height;
          list.push({
            key: segment.key,
            color: segment.color,
            d: band(x(i + 1) + half, source.top, target.height, x(i + 2) - half, target.top, target.height)
          });
          if (leftHeight > 0) {
            list.push({
              key: segment.key,
              color: segment.color,
              d: band(x(i + 1) + half, source.top + target.height, leftHeight, x(i + 1.5) - half, droppedOffset, leftHeight)
            });
            droppedOffset += leftHeight;
          }
        }
      }
      return list;
    });
    const dropped = computed(() => props.steps.slice(0, -1).map((from, i) => {
      const to = props.steps[i + 1];
      const left = total(from) - total(to);
      let offset = droppedTop.value;
      const slices = props.segments.map((segment) => {
        const height = (from.counts[segment.key] - to.counts[segment.key]) * unit.value;
        const rect = {
          segment,
          top: offset,
          height
        };
        offset += height;
        return rect;
      });
      return {
        index: i,
        x: x(i + 1.5),
        left,
        share: Math.round(100 * left / total(from)),
        slices
      };
    }));
    const flowBottom = computed(() => droppedTop.value + Math.max(...dropped.value.map((d) => d.left)) * unit.value + 52);
    // Trace geometry.
    const traceTop = computed(() => flowBottom.value + 64);
    const labelWidth = computed(() => wide.value ? 200 : 162);
    const traceRight = computed(() => width.value - (wide.value ? 112 : 8));
    const tx = (ms) => labelWidth.value + (traceRight.value - labelWidth.value) * ms / props.duration;
    const ticks = computed(() => {
      const count = wide.value ? 4 : 2;
      const step = props.duration / count;
      return Array.from({ length: count + 1 }, (_, i) => ({
        ms: i * step,
        x: tx(i * step)
      }));
    });
    const height = computed(() => traceTop.value + props.spans.length * rowHeight + 12);
    const ribbonOpacity = (key) => active.value === null ? .35 : active.value === key ? .6 : .08;
    const barOpacity = (key) => active.value === null || active.value === key ? 1 : .3;
    return (_ctx, _cache) => {
      return _openBlock(), _createElementBlock(
        "div",
        {
          ref_key: "root",
          ref: root,
          class: "w-full"
        },
        [(_openBlock(), _createElementBlock("svg", {
          viewBox: `0 0 ${width.value} ${height.value}`,
          width: width.value,
          height: height.value,
          class: "block max-w-full",
          style: "color: var(--color-copy)",
          role: "img",
          "aria-label": "A funnel drawn as a flow splitting by campaign creative, and one abandoned session drawn as spans."
        }, [
          _createElementVNode("g", _hoisted_1, [(_openBlock(true), _createElementBlock(
            _Fragment,
            null,
            _renderList(__props.segments, (segment, i) => {
              return _openBlock(), _createElementBlock("g", {
                key: segment.key,
                transform: `translate(${margin + i * (wide.value ? 150 : 104)}, 12)`,
                class: "cursor-pointer",
                onMouseenter: ($event) => active.value = segment.key,
                onMouseleave: ($event) => active.value = null
              }, [_createElementVNode(
                "rect",
                {
                  x: "0",
                  y: "-6",
                  width: "10",
                  height: "10",
                  style: _normalizeStyle(segment.color),
                  fill: "currentColor"
                },
                null,
                4
                /* STYLE */
              ), _createElementVNode(
                "text",
                {
                  x: "16",
                  y: "3"
                },
                _toDisplayString(wide.value ? segment.label : segment.label.replace("spring · ", "")),
                1
                /* TEXT */
              )], 40, [
                "transform",
                "onMouseenter",
                "onMouseleave"
              ]);
            }),
            128
            /* KEYED_FRAGMENT */
          ))]),
          (_openBlock(true), _createElementBlock(
            _Fragment,
            null,
            _renderList(__props.steps, (step, i) => {
              return _openBlock(), _createElementBlock("g", {
                key: step.label,
                "font-size": "10",
                fill: "currentColor"
              }, [_createElementVNode("text", {
                x: x(i + 1),
                y: flowTop - (wide.value ? 8 : 18),
                "text-anchor": "middle",
                style: "color: var(--color-dim)"
              }, [_createTextVNode(
                _toDisplayString(wide.value ? step.label : step.label.replace("Signup · ", "")) + " ",
                1
                /* TEXT */
              ), wide.value ? (_openBlock(), _createElementBlock(
                "tspan",
                {
                  key: 0,
                  "font-family": "var(--font-mono)",
                  style: "color: var(--color-copy)"
                },
                _toDisplayString(format(total(step))),
                1
                /* TEXT */
              )) : _createCommentVNode("v-if", true)], 8, ["x", "y"]), !wide.value ? (_openBlock(), _createElementBlock("text", {
                key: 1,
                x: x(i + 1),
                y: flowTop - 6,
                "text-anchor": "middle",
                "font-family": "var(--font-mono)"
              }, _toDisplayString(format(total(step))), 9, ["x", "y"])) : _createCommentVNode("v-if", true)]);
            }),
            128
            /* KEYED_FRAGMENT */
          )),
          (_openBlock(true), _createElementBlock(
            _Fragment,
            null,
            _renderList(ribbons.value, (ribbon, i) => {
              return _openBlock(), _createElementBlock("path", {
                key: i,
                d: ribbon.d,
                style: _normalizeStyle(ribbon.color),
                fill: "currentColor",
                opacity: ribbonOpacity(ribbon.key),
                class: "transition-opacity"
              }, null, 12, ["d", "opacity"]);
            }),
            128
            /* KEYED_FRAGMENT */
          )),
          (_openBlock(true), _createElementBlock(
            _Fragment,
            null,
            _renderList(sourceBars.value, (bar) => {
              return _openBlock(), _createElementBlock("g", {
                key: bar.segment.key,
                style: _normalizeStyle(bar.segment.color),
                opacity: barOpacity(bar.segment.key)
              }, [_createElementVNode("rect", {
                x: x(0) - barWidth.value / 2,
                y: bar.top,
                width: barWidth.value,
                height: bar.height,
                fill: "currentColor"
              }, null, 8, [
                "x",
                "y",
                "width",
                "height"
              ]), wide.value ? (_openBlock(), _createElementBlock("text", {
                key: 2,
                x: x(0) - barWidth.value / 2 - 6,
                y: bar.top + bar.height / 2 + 3,
                "text-anchor": "end",
                "font-size": "10",
                "font-family": "var(--font-mono)",
                fill: "currentColor"
              }, _toDisplayString(format(__props.steps[0].counts[bar.segment.key])), 9, ["x", "y"])) : _createCommentVNode("v-if", true)], 12, ["opacity"]);
            }),
            128
            /* KEYED_FRAGMENT */
          )),
          (_openBlock(true), _createElementBlock(
            _Fragment,
            null,
            _renderList(__props.steps, (step, i) => {
              return _openBlock(), _createElementBlock("g", { key: "bar" + step.label }, [(_openBlock(true), _createElementBlock(
                _Fragment,
                null,
                _renderList(__props.segments, (segment) => {
                  return _openBlock(), _createElementBlock("rect", {
                    key: segment.key,
                    x: x(i + 1) - barWidth.value / 2,
                    y: slice(step, segment.key).top,
                    width: barWidth.value,
                    height: slice(step, segment.key).height,
                    style: _normalizeStyle(segment.color),
                    fill: "currentColor",
                    opacity: barOpacity(segment.key),
                    class: "cursor-pointer",
                    onMouseenter: ($event) => active.value = segment.key,
                    onMouseleave: ($event) => active.value = null
                  }, null, 44, [
                    "x",
                    "y",
                    "width",
                    "height",
                    "opacity",
                    "onMouseenter",
                    "onMouseleave"
                  ]);
                }),
                128
                /* KEYED_FRAGMENT */
              ))]);
            }),
            128
            /* KEYED_FRAGMENT */
          )),
          (_openBlock(true), _createElementBlock(
            _Fragment,
            null,
            _renderList(dropped.value, (drop) => {
              return _openBlock(), _createElementBlock("g", { key: "drop" + drop.index }, [(_openBlock(true), _createElementBlock(
                _Fragment,
                null,
                _renderList(drop.slices, (rect) => {
                  return _openBlock(), _createElementBlock("rect", {
                    key: rect.segment.key,
                    x: drop.x - barWidth.value / 2,
                    y: rect.top,
                    width: barWidth.value,
                    height: rect.height,
                    style: _normalizeStyle(rect.segment.color),
                    fill: "currentColor",
                    opacity: barOpacity(rect.segment.key)
                  }, null, 12, [
                    "x",
                    "y",
                    "width",
                    "height",
                    "opacity"
                  ]);
                }),
                128
                /* KEYED_FRAGMENT */
              )), _createElementVNode("text", {
                x: drop.x,
                y: droppedTop.value + drop.left * unit.value + 14,
                "text-anchor": "middle",
                "font-size": "10",
                fill: "currentColor",
                style: "color: var(--color-dim)"
              }, [wide.value ? (_openBlock(), _createElementBlock(
                _Fragment,
                { key: 3 },
                [
                  _createTextVNode("left "),
                  _createElementVNode(
                    "tspan",
                    {
                      "font-family": "var(--font-mono)",
                      style: "color: var(--color-copy)"
                    },
                    _toDisplayString(format(drop.left)),
                    1
                    /* TEXT */
                  ),
                  _createTextVNode(" · ")
                ],
                64
                /* STABLE_FRAGMENT */
              )) : _createCommentVNode("v-if", true), _createTextVNode(
                _toDisplayString(drop.share) + " % ",
                1
                /* TEXT */
              )], 8, ["x", "y"])]);
            }),
            128
            /* KEYED_FRAGMENT */
          )),
          _createElementVNode("g", _hoisted_2, [_createElementVNode("text", {
            x: dropped.value[__props.focus].x,
            y: droppedTop.value + dropped.value[__props.focus].left * unit.value + 38,
            "text-anchor": "middle",
            "font-size": "10",
            stroke: "none"
          }, " one of them ", 8, ["x", "y"]), _createElementVNode("line", {
            x1: dropped.value[__props.focus].x,
            x2: dropped.value[__props.focus].x,
            y1: droppedTop.value + dropped.value[__props.focus].left * unit.value + 44,
            y2: traceTop.value - 30,
            "stroke-width": "1",
            "stroke-dasharray": "2 3"
          }, null, 8, [
            "x1",
            "x2",
            "y1",
            "y2"
          ])]),
          _createElementVNode("g", _hoisted_3, [
            _createElementVNode("text", {
              x: margin,
              y: traceTop.value - 26
            }, _toDisplayString(__props.session), 9, ["x", "y"]),
            _createElementVNode("line", {
              x1: labelWidth.value,
              x2: traceRight.value,
              y1: traceTop.value - 4,
              y2: traceTop.value - 4,
              stroke: "var(--color-rule)",
              "stroke-width": "1"
            }, null, 8, [
              "x1",
              "x2",
              "y1",
              "y2"
            ]),
            (_openBlock(true), _createElementBlock(
              _Fragment,
              null,
              _renderList(ticks.value, (tick) => {
                return _openBlock(), _createElementBlock("g", { key: tick.ms }, [_createElementVNode("line", {
                  x1: tick.x,
                  x2: tick.x,
                  y1: traceTop.value - 4,
                  y2: traceTop.value + __props.spans.length * rowHeight,
                  stroke: "var(--color-rule)",
                  "stroke-width": "1"
                }, null, 8, [
                  "x1",
                  "x2",
                  "y1",
                  "y2"
                ]), _createElementVNode("text", {
                  x: tick.x,
                  y: traceTop.value - 8,
                  "text-anchor": tick.ms === __props.duration && !wide.value ? "end" : "middle",
                  "font-family": "var(--font-mono)"
                }, [_createTextVNode(
                  _toDisplayString(format(tick.ms)),
                  1
                  /* TEXT */
                ), tick.ms === 0 ? (_openBlock(), _createElementBlock("tspan", { key: 4 }, " ms")) : _createCommentVNode("v-if", true)], 8, [
                  "x",
                  "y",
                  "text-anchor"
                ])]);
              }),
              128
              /* KEYED_FRAGMENT */
            ))
          ]),
          (_openBlock(true), _createElementBlock(
            _Fragment,
            null,
            _renderList(__props.spans, (span, i) => {
              return _openBlock(), _createElementBlock(
                "g",
                {
                  key: i,
                  style: _normalizeStyle(span.color || "color: var(--color-copy)")
                },
                [
                  i > 0 ? (_openBlock(), _createElementBlock("line", {
                    key: 5,
                    x1: margin,
                    x2: traceRight.value,
                    y1: traceTop.value + i * rowHeight - .5,
                    y2: traceTop.value + i * rowHeight - .5,
                    stroke: "var(--color-rule)",
                    "stroke-width": "1"
                  }, null, 8, [
                    "x1",
                    "x2",
                    "y1",
                    "y2"
                  ])) : _createCommentVNode("v-if", true),
                  _createElementVNode("text", {
                    x: margin + span.depth * (wide.value ? 14 : 7),
                    y: traceTop.value + i * rowHeight + 13,
                    "font-size": "10",
                    "font-family": "var(--font-mono)",
                    fill: "currentColor"
                  }, _toDisplayString(span.label), 9, ["x", "y"]),
                  _createElementVNode("rect", {
                    x: tx(span.start),
                    y: traceTop.value + i * rowHeight + 4,
                    width: Math.max(2, tx(span.start + span.duration) - tx(span.start)),
                    height: "10",
                    fill: "currentColor",
                    opacity: span.color ? .9 : .35
                  }, null, 8, [
                    "x",
                    "y",
                    "width",
                    "opacity"
                  ]),
                  span.note && wide.value ? (_openBlock(), _createElementBlock("text", {
                    key: 6,
                    x: traceRight.value + 8,
                    y: traceTop.value + i * rowHeight + 13,
                    "font-size": "10",
                    "font-family": "var(--font-mono)",
                    fill: "currentColor"
                  }, _toDisplayString(span.note), 9, ["x", "y"])) : _createCommentVNode("v-if", true)
                ],
                4
                /* STYLE */
              );
            }),
            128
            /* KEYED_FRAGMENT */
          ))
        ], 8, [
          "viewBox",
          "width",
          "height"
        ]))],
        512
        /* NEED_PATCH */
      );
    };
  }
};
